'use client';

import { useEffect, useRef, useId } from 'react';

interface StaticPageRendererProps {
  content: string;
  title?: string;
}

/**
 * Sanitize string to remove invalid characters that cause DOMException.
 * Same logic as WidgetRenderer.tsx HTMLWidget.
 */
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    return str
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/[\x80-\x9F]/g, '')
      .replace(/￾|￿/g, '');
  } catch {
    return '';
  }
}

/**
 * StaticPageRenderer — Renders Quintype static page HTML content directly into
 * the page DOM (no iframe). Scripts inserted via innerHTML never execute, so
 * they're extracted and re-created manually, mirroring CustomAdWidget in
 * WidgetRenderer.tsx.
 */
export default function StaticPageRenderer({ content, title }: StaticPageRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '').replace(/^(\d)/, 'sp$1');
  const widgetId = `static-page-${uniqueId}`;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const sanitizedHtml = sanitizeForDOM(content);

    // Rewrite hardcoded dinamani.com domain links in href attributes
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const rewrittenHtml = currentOrigin
      ? sanitizedHtml.replace(
          /href\s*=\s*"(https?:\/\/(?:www\.)?dinamani\.com)([^"]*)"/gi,
          (_, _domain, path) => `href="${currentOrigin}${path}"`
        )
      : sanitizedHtml;

    // If content is a full HTML document, pull out head + body contents
    // (styles/links from head still work when placed anywhere in the DOM).
    const isFullDocument = /<!DOCTYPE|<html[\s>]/i.test(rewrittenHtml);
    let innerHtml = rewrittenHtml;

    if (isFullDocument) {
      const headMatch = rewrittenHtml.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const bodyMatch = rewrittenHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      innerHtml = (headMatch ? headMatch[1] : '') + (bodyMatch ? bodyMatch[1] : rewrittenHtml);
    }

    // Parse in a detached container so embedded scripts don't get inserted
    // (and silently no-op'd) before we've had a chance to extract them.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = innerHtml;

    const scripts = Array.from(tempDiv.querySelectorAll('script'));
    const scriptsToExecute: { src?: string; content?: string }[] = [];
    scripts.forEach((script) => {
      if (script.src) {
        scriptsToExecute.push({ src: script.src });
      } else if (script.textContent) {
        scriptsToExecute.push({ content: sanitizeForDOM(script.textContent) });
      }
      script.remove();
    });

    container.innerHTML = tempDiv.innerHTML;

    scriptsToExecute.forEach((scriptInfo) => {
      try {
        if (scriptInfo.src) {
          if (!document.querySelector(`script[src="${scriptInfo.src}"]`)) {
            const newScript = document.createElement('script');
            newScript.src = scriptInfo.src;
            newScript.async = true;
            document.body.appendChild(newScript);
          }
        } else if (scriptInfo.content) {
          try {
            const fn = new Function(scriptInfo.content);
            fn();
          } catch {
            try {
              const newScript = document.createElement('script');
              newScript.textContent = scriptInfo.content;
              document.body.appendChild(newScript);
              newScript.remove();
            } catch {
              // Silently fail if script cannot be executed
            }
          }
        }
      } catch {
        // Silently handle script creation errors
      }
    });

    return () => {
      container.innerHTML = '';
    };
  }, [content]);

  return (
    <div
      ref={containerRef}
      id={widgetId}
      aria-label={title}
      className="static-page-content"
      style={{ width: '100%', maxWidth: '100%' }}
    />
  );
}
